import { Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AttachmentItem,
  CardComponent,
} from '@sportello-balneari/shared-components';
import { wktToGeoJSON } from '@terraformer/wkt';
import { FeatureCollection, Position } from 'geojson';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { TenderContextService } from '../../../services/compiling/state/tender-context.service';
import { TenderMapV2Component } from './tender-map-v2/tender-map-v2.component';

type LotGeometry =
  | { type: 'Point'; coordinates: [number, number] }
  | { type: 'Polygon'; coordinates: Position[][] }
  | { type: 'MultiPolygon'; coordinates: Position[][][] };

function getPolygonCentroid(coordinates: Position[][]): [number, number] {
  const outerRing = coordinates[0];
  let sumLng = 0;
  let sumLat = 0;
  for (const [lng, lat] of outerRing) {
    sumLng += lng;
    sumLat += lat;
  }
  return [sumLng / outerRing.length, sumLat / outerRing.length];
}

function extractLotInfo(areaWkt: string): {
  center: [number, number];
  marker: FeatureCollection;
} | null {
  try {
    const geo = wktToGeoJSON(areaWkt) as LotGeometry;

    if (geo.type === 'Point') {
      const coords = geo.coordinates as [number, number];
      return {
        center: coords,
        marker: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: coords },
              properties: {},
            },
          ],
        },
      };
    }

    if (geo.type === 'Polygon') {
      const centroid = getPolygonCentroid(geo.coordinates);
      return {
        center: centroid,
        marker: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: centroid },
              properties: {},
            },
          ],
        },
      };
    }

    if (geo.type === 'MultiPolygon') {
      const centroid = getPolygonCentroid(geo.coordinates[0]);
      return {
        center: centroid,
        marker: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: centroid },
              properties: {},
            },
          ],
        },
      };
    }
  } catch (e) {
    console.error(e);
  }
  return null;
}

@Component({
  selector: 'lib-tender-lot-details',
  template: `
    @let lot = tenderContext.tenderLotDetails();
    @if (lot) {
      <lib-card class="w-full">
        <ng-container title>Lotto</ng-container>
        <ng-container content>
          <div class="flex flex-col gap-10">
            <!-- 1. Anagrafica e Localizzazione -->
            <div class="flex flex-col gap-6">
              <div class="text-lg font-semibold">
                1. Anagrafica e Localizzazione
              </div>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="denomination"
                    >Denominazione</label
                  >
                  <input
                    pInputText
                    id="denomination"
                    type="text"
                    [ngModel]="lot.name"
                    [disabled]="true"
                    class="w-full bg-gray-50 text-gray-900 opacity-100"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="pua"
                    >PUA/PUD</label
                  >
                  <input
                    pInputText
                    id="pua"
                    type="text"
                    [ngModel]="lot.pua"
                    [disabled]="true"
                    class="w-full bg-gray-50 text-gray-900 opacity-100"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="address"
                    >Indirizzo</label
                  >
                  <input
                    pInputText
                    id="address"
                    type="text"
                    [ngModel]="lot.address ?? 'N.D.'"
                    [disabled]="true"
                    class="w-full bg-gray-50"
                  />
                </div>

                @if (lotInfo()) {
                  <div class="col-span-full flex flex-col gap-2">
                    <span class="font-semibold text-gray-700">Area</span>
                    <div
                      class="h-96 w-full overflow-hidden rounded-2xl border border-gray-300 shadow-inner"
                    >
                      <lib-tender-map-v2
                        [center]="lotInfo()?.center"
                        [area]="lotArea()"
                        [tenderLots]="lotInfo()?.marker"
                        [minZoom]="10"
                        [zoomDisabled]="true"
                      />
                    </div>
                  </div>
                }

                <div class="col-span-full flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="description"
                    >Descrizione</label
                  >
                  <textarea
                    pTextarea
                    id="description"
                    [ngModel]="lot.description ?? 'N.D.'"
                    [disabled]="true"
                    [autoResize]="true"
                    rows="6"
                    class="w-full bg-gray-50"
                  ></textarea>
                </div>

                <div class="col-span-full flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="cadastral"
                    >Riferimenti catastali</label
                  >
                  <input
                    pInputText
                    id="cadastral"
                    type="text"
                    [ngModel]="lot.landRegistryReferences ?? 'N.D.'"
                    [disabled]="true"
                    class="w-full bg-gray-50"
                  />
                </div>

                <div class="col-span-full flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="borders"
                    >Confini</label
                  >
                  <textarea
                    pTextarea
                    id="borders"
                    [ngModel]="lot.borders ?? 'N.D.'"
                    [disabled]="true"
                    [autoResize]="true"
                    rows="6"
                    class="w-full bg-gray-50"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- 2. Dimensioni -->
            <div class="flex flex-col gap-6 border-t border-gray-100 pt-6">
              <div class="text-lg font-semibold">2. Dimensioni</div>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="totalArea"
                    >Superficie totale (mq)</label
                  >
                  <p-inputnumber
                    [autofocus]="false"
                    id="totalArea"
                    [ngModel]="lot.totalArea"
                    placeholder="N.D."
                    [disabled]="true"
                    suffix=" mq"
                    styleClass="w-full"
                    class="w-full"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="uncoveredArea"
                    >Superficie area scoperta (mq)</label
                  >
                  <p-inputnumber
                    [autofocus]="false"
                    [ngModel]="lot.uncoveredArea"
                    placeholder="N.D."
                    [disabled]="true"
                    suffix=" mq"
                    styleClass="w-full"
                    class="w-full"
                    id="uncoveredArea"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label for="lot-builtArea" class="font-semibold"
                    >Superficie opere di difficile rimozione (mq)</label
                  >
                  <p-inputnumber
                    [autofocus]="false"
                    inputId="lot-builtArea"
                    [ngModel]="lot.hardToRemoveArea"
                    placeholder="N.D."
                    [disabled]="true"
                    suffix=" mq"
                    styleClass="w-full"
                    class="w-full"
                  />
                </div>

                <div class="flex flex-col gap-2">
                  <label for="lot-waterArea" class="font-semibold"
                    >Superficie specchi acquei (mq)</label
                  >
                  <p-inputnumber
                    [autofocus]="false"
                    inputId="lot-waterArea"
                    [ngModel]="lot.waterArea"
                    placeholder="N.D."
                    [disabled]="true"
                    suffix=" mq"
                    styleClass="w-full"
                    class="w-full"
                  />
                </div>
              </div>
            </div>

            <!-- 3. Dati Economici -->
            <div class="flex flex-col gap-6 border-t border-gray-100 pt-6">
              <div class="text-lg font-semibold">3. Dati Economici</div>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="annualBaseFee"
                    >Canone annuo a base d'asta (€)</label
                  >
                  <p-inputnumber
                    [autofocus]="false"
                    id="annualBaseFee"
                    [ngModel]="lot.annualBaseFee"
                    placeholder="N.D."
                    mode="currency"
                    currency="EUR"
                    [disabled]="true"
                    styleClass="w-full"
                    class="w-full"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label for="lot-tax" class="font-semibold"
                    >Imposta regionale (€)</label
                  >
                  <p-inputnumber
                    [autofocus]="false"
                    inputId="lot-tax"
                    [ngModel]="lot.regionalTax"
                    placeholder="N.D."
                    mode="currency"
                    currency="EUR"
                    [disabled]="true"
                    styleClass="w-full"
                    class="w-full"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label for="lot-provisionalBail" class="font-semibold"
                    >Cauzione provvisoria (€)</label
                  >
                  <p-inputnumber
                    [autofocus]="false"
                    inputId="lot-provisionalBail"
                    [ngModel]="lot.provisionalBail"
                    placeholder="N.D."
                    mode="currency"
                    currency="EUR"
                    [disabled]="true"
                    styleClass="w-full"
                    class="w-full"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="regionalTax"
                    >Classificazione valenza turistica</label
                  >
                  <p-select
                    [autofocus]="false"
                    id="regionalTax"
                    [options]="categoryOptions"
                    [ngModel]="lot.touristValueClassification"
                    placeholder="N.D."
                    optionLabel="label"
                    optionValue="value"
                    [disabled]="true"
                    styleClass="w-full"
                    class="w-full"
                  />
                </div>
              </div>
            </div>

            <!-- 4. Durata Concessione -->
            <div class="flex flex-col gap-6 border-t border-gray-100 pt-6">
              <div class="text-lg font-semibold">4. Durata Concessione</div>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="flex flex-col gap-2">
                  <label
                    class="font-semibold text-gray-700"
                    for="concessionPeriodYears"
                    >Durata concessione minima (anni)</label
                  >
                  <p-inputnumber
                    [autofocus]="false"
                    id="concessionPeriodYears"
                    [ngModel]="lot.concessionPeriodYears"
                    placeholder="N.D."
                    [disabled]="true"
                    styleClass="w-full"
                    class="w-full"
                  />
                  @if (concessionMinWarning()) {
                    <div
                      class="flex items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800"
                    >
                      <i
                        class="pi pi-exclamation-triangle flex-shrink-0 text-yellow-500"
                      ></i>
                      <span>{{ concessionMinWarning() }}</span>
                    </div>
                  }
                </div>
                <div class="flex flex-col gap-2">
                  <label
                    class="font-semibold text-gray-700"
                    for="concessionPeriodMaxYears"
                    >Durata concessione massima (anni)</label
                  >
                  <p-inputnumber
                    [autofocus]="false"
                    id="concessionPeriodMaxYears"
                    [ngModel]="lot.concessionPeriodMaxYears"
                    placeholder="N.D."
                    [disabled]="true"
                    styleClass="w-full"
                    class="w-full"
                  />
                  @if (concessionMaxWarning()) {
                    <div
                      class="flex items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800"
                    >
                      <i
                        class="pi pi-exclamation-triangle flex-shrink-0 text-yellow-500"
                      ></i>
                      <span>{{ concessionMaxWarning() }}</span>
                    </div>
                  }
                </div>
              </div>
            </div>

            <!-- Attachments -->
            <div class="flex flex-col gap-4 border-t border-gray-100 pt-6">
              <span class="font-semibold text-gray-700"
                >Documentazione ufficiale</span
              >
              @if (lot.attachments && lot.attachments!.length > 0) {
                <ul class="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                  @for (attachment of lot.attachments; track $index) {
                    <lib-attachment-item
                      [fileName]="attachment.fileName"
                      [downloadUrl]="attachment.url"
                      [showDownload]="true"
                    />
                  }
                </ul>
              } @else {
                <div
                  class="rounded-xl border-2 border-dashed border-gray-200 py-6 text-center text-sm italic text-gray-400"
                >
                  Nessuna documentazione ufficiale.
                </div>
              }
            </div>
          </div>
        </ng-container>
      </lib-card>
    }
  `,
  imports: [
    TenderMapV2Component,
    TextareaModule,
    SelectModule,
    CardComponent,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    AttachmentItem,
  ],
})
export class TenderLotDetailsComponent {
  protected readonly tenderContext = inject(TenderContextService);

  categoryOptions = [
    { label: 'A', value: 'A' },
    { label: 'B', value: 'B' },
    { label: 'C', value: 'C' },
  ];

  readonly concessionMinWarning = computed(() => {
    const lotVal =
      this.tenderContext.tenderLotDetails()?.concessionPeriodYears;
    const tenderVal = this.tenderContext.tenderInfo()?.concessionPeriodYears;
    if (lotVal != null && tenderVal != null && lotVal !== tenderVal) {
      return `La durata minima di questo lotto è diversa da quella del bando (${tenderVal} anni).`;
    }
    return null;
  });

  readonly concessionMaxWarning = computed(() => {
    const lotVal =
      this.tenderContext.tenderLotDetails()?.concessionPeriodMaxYears;
    const tenderVal =
      this.tenderContext.tenderInfo()?.concessionPeriodMaxYears;
    if (lotVal != null && tenderVal != null && lotVal !== tenderVal) {
      return `La durata massima di questo lotto è diversa da quella del bando (${tenderVal} anni).`;
    }
    return null;
  });

  lotInfo = computed(() => {
    const lot = this.tenderContext.tenderLotDetails();
    if (lot?.area) {
      return extractLotInfo(lot.area);
    }
    return null;
  });

  lotArea = computed(() => {
    const lot = this.tenderContext.tenderLotDetails();
    if (lot?.area) {
      try {
        const geo = wktToGeoJSON(lot.area) as LotGeometry;
        if (geo.type === 'Point') return geo.coordinates;
        if (geo.type === 'Polygon') return [geo.coordinates];
        if (geo.type === 'MultiPolygon') return geo.coordinates;
      } catch (e) {
        console.error(e);
      }
    }
    return null;
  });
}
